import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useStores } from '../hooks/use-stores';
import { palette } from '@awarego-ui/components';

const DEFAULT_PRIMARY = palette.evergreen.aspargus;
const DEFAULT_SECONDARY = palette.vibrant.raspberry;
const DEFAULT_FONT =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

function hexForUserway(color) {
    if (!color || typeof color !== 'string') return '';
    return color.replace(/^#/, '').replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
}

/**
 * See packages/my/PLAYER_CSS.md for variables, selectors, and the
 * allowlisted properties stored on ui.my.customCss.
 */
function BrandingCss() {
    const { brandingStore } = useStores();
    const location = useLocation();
    const isScormRoute = (location.pathname || '').includes('/cmi5/');
    const {
        brandingLoaded,
        theme,
        fontFamily,
        fontUrl,
        customCss,
        canFastForward,
        hideClose,
    } = brandingStore;

    const primary = theme?.primary || DEFAULT_PRIMARY;
    const secondary = theme?.secondary || DEFAULT_SECONDARY;
    const family = fontFamily || DEFAULT_FONT;
    const userwayColor = hexForUserway(primary);

    const frameworkCss = useMemo(
        () => `
:root {
  --ag-color-primary: ${primary};
  --ag-color-secondary: ${secondary};
  --ag-font-family: ${family};
}
html, body, #root {
  font-family: var(--ag-font-family);
}
.ag-hide-player-close .ag-player-close {
  display: none !important;
}
.ag-no-fast-forward .jw-slider-time,
.ag-no-fast-forward .jw-slider-time * {
  pointer-events: none !important;
}
#userwayAccessibilityIcon,
.userway_p1,
.uai-trigger {
  background-color: var(--ag-color-primary) !important;
}
`,
        [primary, secondary, family]
    );

    useEffect(() => {
        if (!brandingLoaded) return;
        const root = document.documentElement;
        root.classList.toggle(
            'ag-hide-player-close',
            !!hideClose || isScormRoute
        );
        root.classList.toggle('ag-no-fast-forward', !canFastForward);
        if (userwayColor) {
            window.userwayOptions = {
                ...(window.userwayOptions || {}),
                color: userwayColor,
            };
        }
        return () => {
            root.classList.remove('ag-hide-player-close');
            root.classList.remove('ag-no-fast-forward');
        };
    }, [brandingLoaded, hideClose, canFastForward, userwayColor, isScormRoute]);

    if (!brandingLoaded) return null;

    return (
        <Helmet>
            {fontUrl ? <link rel="stylesheet" href={fontUrl} /> : null}
            <style type="text/css">{frameworkCss}</style>
            {customCss ? <style type="text/css">{customCss}</style> : null}
        </Helmet>
    );
}

export default observer(BrandingCss);
